import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { CollectionStatus } from '@/features/lastMile/types';
import type { LookupData } from '@/services/last-mile/customerCollectionsApi';

export interface PieceDamageDetail {
    isSelected: boolean;
    damageLocation: string;
}

interface CustomerCollectionState {
    collectionStatus: CollectionStatus;
    shipment: LookupData | null;
    receiverType: string;
    receiverFullName: string;
    isManualSignatureAcknowledged: boolean;
    signatureImage: string | null;
    isShipmentDamaged: boolean;
    pieceDamages: Record<string, PieceDamageDetail>;
    isSignatureModalOpen: boolean;
    isRefusalModalOpen: boolean;
    refusalReason: string;
    otherRefusalReason: string;
    actionTimestamp: string | null;
    isReceiverAuthorizationAcknowledged: boolean;
    msg: string | null;
}

const initialState: CustomerCollectionState = {
    collectionStatus: 'NEW',
    shipment: null,
    receiverType: '',
    receiverFullName: '',
    isManualSignatureAcknowledged: false,
    signatureImage: null,
    isShipmentDamaged: false,
    pieceDamages: {},
    isSignatureModalOpen: false,
    isRefusalModalOpen: false,
    refusalReason: '',
    otherRefusalReason: '',
    actionTimestamp: null,
    isReceiverAuthorizationAcknowledged: false,
};

const customerCollectionSlice = createSlice({
    name: 'customerCollection',
    initialState,
    reducers: {
        setCollectionStatus(state, action: PayloadAction<CollectionStatus>) {
            state.collectionStatus = action.payload;
        },
        setShipmentFromLookup(state, action: PayloadAction<LookupData>) {
            state.shipment = action.payload;
        },
        setReceiverType(state, action: PayloadAction<string>) {
            state.receiverType = action.payload;
        },
        setReceiverFullName(state, action: PayloadAction<string>) {
            state.receiverFullName = action.payload;
        },
        setManualSignatureAcknowledged(state, action: PayloadAction<boolean>) {
            state.isManualSignatureAcknowledged = action.payload;
        },
        setSignatureImage(state, action: PayloadAction<string | null>) {
            state.signatureImage = action.payload;
        },
        setActionTimestamp(state, action: PayloadAction<string | null>) {
            state.actionTimestamp = action.payload;
        },
        setShipmentDamaged(state, action: PayloadAction<boolean>) {
            state.isShipmentDamaged = action.payload;
            // clear the follow-up assessment whenever damage is unchecked
            if (!action.payload) {
                state.pieceDamages = {};
            }
        },
        setPieceDamageSelection(
            state,
            action: PayloadAction<{ pieceId: string; isSelected: boolean }>,
        ) {
            const { pieceId, isSelected } = action.payload;
            const existing = state.pieceDamages[pieceId];
            state.pieceDamages[pieceId] = {
                isSelected,
                // clear any previously chosen location once a piece is unchecked
                damageLocation: isSelected ? (existing?.damageLocation ?? '') : '',
            };
        },
        setPieceDamageLocation(
            state,
            action: PayloadAction<{ pieceId: string; damageLocation: string }>,
        ) {
            const { pieceId, damageLocation } = action.payload;
            state.pieceDamages[pieceId] = {
                isSelected: true,
                damageLocation,
            };
        },
        openSignatureModal(state) {
            state.isSignatureModalOpen = true;
        },
        closeSignatureModal(state) {
            state.isSignatureModalOpen = false;
        },
        openRefusalModal(state) {
            state.isRefusalModalOpen = true;
        },
        closeRefusalModal(state) {
            state.isRefusalModalOpen = false;
        },
        setRefusalReason(state, action: PayloadAction<string>) {
            state.refusalReason = action.payload;
        },
        setOtherRefusalReason(state, action: PayloadAction<string>) {
            state.otherRefusalReason = action.payload;
        },
        setReceiverAuthorizationAcknowledged(state, action: PayloadAction<boolean>) {
            state.isReceiverAuthorizationAcknowledged = action.payload;
        },
        resetCustomerCollection() {
            return initialState;
        },
    },
});

export const {
    setCollectionStatus,
    setShipmentFromLookup,
    setReceiverType,
    setReceiverFullName,
    setManualSignatureAcknowledged,
    setSignatureImage,
    setActionTimestamp,
    setShipmentDamaged,
    setPieceDamageSelection,
    setPieceDamageLocation,
    openSignatureModal,
    closeSignatureModal,
    openRefusalModal,
    closeRefusalModal,
    setRefusalReason,
    setOtherRefusalReason,
    setReceiverAuthorizationAcknowledged,
    resetCustomerCollection,
} = customerCollectionSlice.actions;

export default customerCollectionSlice.reducer;
